import React, { useState, useCallback } from "react";
import { Bold, Heading1, Heading2, Heading3, List, ListOrdered, ImageIcon, HelpCircle, Link2, Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Upload, Check, AlertTriangle, X, Search } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_TYPES = [
  { type: "paragraph", label: "Paragraph", icon: "P" },
  { type: "h2", label: "Heading 2", icon: "H2" },
  { type: "h3", label: "Heading 3", icon: "H3" },
  { type: "bullet_list", label: "Bullet List", icon: "•" },
  { type: "numbered_list", label: "Numbered List", icon: "1." },
  { type: "image", label: "Image", icon: "IMG" },
  { type: "faq", label: "FAQ", icon: "?" },
];

const INTERNAL_PAGES = [
  { url: "/", label: "Homepage" },
  { url: "/virtual-staging-uk", label: "Virtual Staging" },
  { url: "/3d-property-rendering-uk", label: "3D Renders" },
  { url: "/order", label: "Order" },
  { url: "/services", label: "Services" },
  { url: "/blog", label: "Blog" },
];

function createBlock(type) {
  switch (type) {
    case "paragraph": return { type, text: "" };
    case "h2": return { type, text: "" };
    case "h3": return { type, text: "" };
    case "bullet_list": return { type, items: [""] };
    case "numbered_list": return { type, items: [""] };
    case "image": return { type, url: "", alt: "", filename: "" };
    case "faq": return { type, question: "", answer: "" };
    default: return { type: "paragraph", text: "" };
  }
}

// --- Block Renderers ---
function ParagraphBlock({ block, onChange }) {
  // Support inline bold with **text** and links with [text](url)
  return (
    <textarea
      value={block.text}
      onChange={(e) => onChange({ ...block, text: e.target.value })}
      rows={3}
      className="w-full px-3 py-2 text-sm text-[#0D2D54] border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none resize-y"
      placeholder="Write paragraph text... Use **bold** for bold text, [link text](url) for links"
    />
  );
}

function HeadingBlock({ block, onChange, level }) {
  return (
    <input
      type="text"
      value={block.text}
      onChange={(e) => onChange({ ...block, text: e.target.value })}
      className={`w-full px-3 py-2 border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none ${
        level === 2 ? "text-lg font-semibold text-[#0D2D54]" : "text-base font-medium text-[#0D2D54]"
      }`}
      placeholder={`Heading ${level}...`}
    />
  );
}

function ListBlock({ block, onChange, ordered }) {
  const addItem = () => onChange({ ...block, items: [...block.items, ""] });
  const removeItem = (i) => {
    if (block.items.length <= 1) return;
    onChange({ ...block, items: block.items.filter((_, idx) => idx !== i) });
  };
  const updateItem = (i, val) => {
    const items = [...block.items];
    items[i] = val;
    onChange({ ...block, items });
  };

  return (
    <div className="space-y-1">
      {block.items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280] w-5 text-right flex-shrink-0">
            {ordered ? `${i + 1}.` : "•"}
          </span>
          <input
            type="text"
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm text-[#0D2D54] border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
            placeholder="List item..."
          />
          <button type="button" onClick={() => removeItem(i)} className="text-[#6B7280] hover:text-red-500">
            <X size={12} />
          </button>
        </div>
      ))}
      <button type="button" onClick={addItem} className="text-xs text-[#0D2D54] hover:underline ml-7">
        + Add item
      </button>
    </div>
  );
}

function ImageBlock({ block, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      onChange({
        ...block,
        url: `${API}/files/${res.data.storage_path}`,
        filename: file.name,
      });
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      {block.url ? (
        <div className="relative">
          <img src={block.url} alt={block.alt || ""} className="w-full max-h-48 object-cover border border-[#E8E8E4]" />
          <button
            type="button"
            onClick={() => onChange({ ...block, url: "", filename: "" })}
            className="absolute top-1 right-1 bg-white text-red-500 text-xs px-2 py-1 border shadow-sm"
          >
            Remove
          </button>
        </div>
      ) : (
        <label className="block border-2 border-dashed border-[#E8E8E4] p-6 text-center cursor-pointer hover:border-[#0D2D54] transition-colors">
          <Upload size={20} className="mx-auto mb-1 text-[#6B7280]" />
          <span className="text-xs text-[#6B7280]">{uploading ? "Uploading..." : "Click to upload image"}</span>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleUpload} />
        </label>
      )}
      <input
        type="text"
        value={block.alt}
        onChange={(e) => onChange({ ...block, alt: e.target.value })}
        className="w-full px-3 py-2 text-sm text-[#0D2D54] border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
        placeholder="Alt text (required for SEO) — describe the image"
      />
      {block.url && !block.alt && (
        <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={12} /> Alt text is required</p>
      )}
    </div>
  );
}

function FAQBlock({ block, onChange }) {
  return (
    <div className="space-y-2 bg-[#FAFAF8] p-3 border border-[#E8E8E4]">
      <input
        type="text"
        value={block.question}
        onChange={(e) => onChange({ ...block, question: e.target.value })}
        className="w-full px-3 py-2 text-sm font-medium text-[#0D2D54] border border-[#E8E8E4] bg-white focus:border-[#0D2D54] focus:outline-none"
        placeholder="FAQ Question..."
      />
      <textarea
        value={block.answer}
        onChange={(e) => onChange({ ...block, answer: e.target.value })}
        rows={2}
        className="w-full px-3 py-2 text-sm text-[#0D2D54] border border-[#E8E8E4] bg-white focus:border-[#0D2D54] focus:outline-none resize-y"
        placeholder="FAQ Answer..."
      />
    </div>
  );
}

// --- Internal Link Inserter ---
function InternalLinkTool({ blogPosts, onInsert }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const allPages = [
    ...INTERNAL_PAGES,
    ...blogPosts.map((p) => ({ url: `/blog/${p.slug}`, label: p.title })),
  ];

  const filtered = allPages.filter((p) =>
    p.label.toLowerCase().includes(search.toLowerCase())
  );

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-[#0D2D54] hover:underline">
        <Link2 size={12} /> Insert internal link
      </button>
    );
  }

  return (
    <div className="border border-[#E8E8E4] bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Search size={14} className="text-[#6B7280]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 text-xs border-b border-[#E8E8E4] py-1 focus:outline-none focus:border-[#0D2D54]"
          placeholder="Search pages..."
          autoFocus
        />
        <button type="button" onClick={() => setOpen(false)} className="text-[#6B7280]"><X size={14} /></button>
      </div>
      <div className="max-h-32 overflow-y-auto space-y-1">
        {filtered.map((page) => (
          <button
            key={page.url}
            type="button"
            onClick={() => { onInsert(page); setOpen(false); setSearch(""); }}
            className="block w-full text-left px-2 py-1.5 text-xs hover:bg-[#FAFAF8] transition-colors"
          >
            <span className="text-[#0D2D54] font-medium">{page.label}</span>
            <span className="text-[#6B7280] ml-2">{page.url}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- SEO Checklist ---
function SEOChecklist({ data }) {
  const checks = [
    { label: "H1 (title) present", pass: !!data.title?.trim() },
    { label: "Minimum 2 H2 blocks", pass: data.h2Count >= 2 },
    { label: "Meta title completed", pass: !!data.metaTitle?.trim() },
    { label: "Meta description completed", pass: !!data.metaDescription?.trim() },
    { label: "Primary keyword set", pass: !!data.primaryKeyword?.trim() },
    { label: "Keyword in H1", pass: data.keywordInTitle },
    { label: "Keyword in meta title", pass: data.keywordInMetaTitle },
    { label: "Keyword in first paragraph", pass: data.keywordInFirstParagraph },
    { label: "Minimum 2 internal links", pass: data.internalLinkCount >= 2 },
    { label: "Featured image added", pass: !!data.featuredImage },
    { label: "All images have alt text", pass: data.allImagesHaveAlt },
    { label: "FAQ section added", pass: data.faqCount > 0, recommended: true },
    { label: "Minimum 600 words", pass: data.wordCount >= 600 },
  ];

  const required = checks.filter((c) => !c.recommended);
  const passCount = required.filter((c) => c.pass).length;
  const allRequired = required.every((c) => c.pass);

  return (
    <div className="border border-[#E8E8E4] p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-[#0D2D54]">Pre-Publish SEO Checklist</h4>
        <span className={`text-xs px-2 py-1 font-medium ${allRequired ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
          {allRequired ? "Ready to publish" : `${passCount}/${required.length} passed`}
        </span>
      </div>
      <div className="space-y-1.5">
        {checks.map((check, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            {check.pass ? (
              <Check size={14} className="text-green-600 flex-shrink-0" />
            ) : check.recommended ? (
              <AlertTriangle size={14} className="text-yellow-500 flex-shrink-0" />
            ) : (
              <X size={14} className="text-red-500 flex-shrink-0" />
            )}
            <span className={check.pass ? "text-[#0D2D54]" : check.recommended ? "text-yellow-700" : "text-red-600"}>
              {check.label} {check.recommended && !check.pass ? "(recommended)" : ""}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-[#E8E8E4] flex gap-4 text-xs text-[#6B7280]">
        <span>{data.wordCount} words</span>
        <span>{data.readingTime} min read</span>
      </div>
    </div>
  );
}

// ===================== MAIN EDITOR =====================
export default function BlogEditor({ post, blogPosts, onSave, onCancel, token }) {
  const [title, setTitle] = useState(post?.title || "");
  const [slug, setSlug] = useState(post?.slug || "");
  const [blocks, setBlocks] = useState(post?.content_blocks || [createBlock("paragraph")]);
  const [metaTitle, setMetaTitle] = useState(post?.meta_title || "");
  const [metaDescription, setMetaDescription] = useState(post?.meta_description || "");
  const [primaryKeyword, setPrimaryKeyword] = useState(post?.primary_keyword || "");
  const [secondaryKeywords, setSecondaryKeywords] = useState(post?.secondary_keywords?.join(", ") || "");
  const [featuredImage, setFeaturedImage] = useState(post?.featured_image || "");
  const [featuredImageAlt, setFeaturedImageAlt] = useState(post?.featured_image_alt || "");
  const [category, setCategory] = useState(post?.category || "Virtual Staging");
  const [tags, setTags] = useState(post?.tags?.join(", ") || "");
  const [ogTitle, setOgTitle] = useState(post?.og_title || "");
  const [ogDescription, setOgDescription] = useState(post?.og_description || "");
  const [noindex, setNoindex] = useState(post?.noindex || false);
  const [canonicalUrl, setCanonicalUrl] = useState(post?.canonical_url || "");
  const [includeInSitemap, setIncludeInSitemap] = useState(post?.include_in_sitemap ?? true);
  const [published, setPublished] = useState(post?.published ?? false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(null);

  const generateSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleTitleChange = (val) => {
    setTitle(val);
    if (!post) setSlug(generateSlug(val));
  };

  // Block operations
  const updateBlock = (i, newBlock) => {
    const updated = [...blocks];
    updated[i] = newBlock;
    setBlocks(updated);
  };
  const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const updated = [...blocks];
    [updated[i], updated[j]] = [updated[j], updated[i]];
    setBlocks(updated);
  };
  const addBlockAt = (i, type) => {
    const updated = [...blocks];
    updated.splice(i + 1, 0, createBlock(type));
    setBlocks(updated);
    setShowAddBlock(null);
  };

  // Featured image upload
  const handleFeaturedUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFeatured(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      setFeaturedImage(`${API}/files/${res.data.storage_path}`);
      toast.success("Featured image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploadingFeatured(false);
  };

  // Internal link insert
  const handleInsertLink = (page) => {
    // Add a paragraph block with the link
    setBlocks([...blocks, { type: "paragraph", text: `[${page.label}](${page.url})` }]);
    toast.success(`Link to "${page.label}" added`);
  };

  // Compute SEO data
  const getPlainText = useCallback(() => {
    return blocks.map((b) => {
      if (b.text) return b.text;
      if (b.items) return b.items.join(" ");
      if (b.question && b.answer) return `${b.question} ${b.answer}`;
      return "";
    }).join(" ");
  }, [blocks]);

  const wordCount = getPlainText().split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.round(wordCount / 200));
  const h2Count = blocks.filter((b) => b.type === "h2").length;
  const faqCount = blocks.filter((b) => b.type === "faq").length;
  const imageBlocks = blocks.filter((b) => b.type === "image");
  const allImagesHaveAlt = imageBlocks.length === 0 || imageBlocks.every((b) => b.alt?.trim());
  const internalLinkCount = getPlainText().match(/\]\(\//g)?.length || 0;

  const kw = primaryKeyword.toLowerCase().trim();
  const keywordInTitle = kw && title.toLowerCase().includes(kw);
  const keywordInMetaTitle = kw && (metaTitle || title).toLowerCase().includes(kw);
  const firstParagraph = blocks.find((b) => b.type === "paragraph")?.text || "";
  const keywordInFirstParagraph = kw && firstParagraph.toLowerCase().includes(kw);

  const seoData = {
    title, metaTitle: metaTitle || title, metaDescription, primaryKeyword,
    h2Count, faqCount, featuredImage, allImagesHaveAlt, internalLinkCount,
    keywordInTitle, keywordInMetaTitle, keywordInFirstParagraph,
    wordCount, readingTime,
  };

  // Convert blocks to content string for backward compat
  const blocksToContent = () => {
    return blocks.map((b) => {
      switch (b.type) {
        case "paragraph": return b.text;
        case "h2": return `## ${b.text}`;
        case "h3": return `### ${b.text}`;
        case "bullet_list": return b.items.map((i) => `- ${i}`).join("\n");
        case "numbered_list": return b.items.map((item, i) => `${i + 1}. ${item}`).join("\n");
        case "image": return b.url ? `![${b.alt || ""}](${b.url})` : "";
        case "faq": return `**Q: ${b.question}**\n\n${b.answer}`;
        default: return "";
      }
    }).filter(Boolean).join("\n\n");
  };

  const handleSubmit = () => {
    if (!title.trim() || !slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    if (blocks.length === 0) {
      toast.error("Add at least one content block");
      return;
    }

    const faqItems = blocks.filter((b) => b.type === "faq").map((b) => ({ question: b.question, answer: b.answer }));

    onSave({
      title,
      slug,
      content: blocksToContent(),
      content_blocks: blocks,
      excerpt: metaDescription || firstParagraph.substring(0, 155),
      featured_image: featuredImage,
      featured_image_alt: featuredImageAlt,
      meta_title: metaTitle,
      meta_description: metaDescription,
      primary_keyword: primaryKeyword,
      secondary_keywords: secondaryKeywords.split(",").map((s) => s.trim()).filter(Boolean),
      og_title: ogTitle || metaTitle || title,
      og_description: ogDescription || metaDescription,
      category,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      faq_items: faqItems,
      noindex,
      canonical_url: canonicalUrl,
      include_in_sitemap: includeInSitemap,
      published,
      author: "The Secret Seller",
    });
  };

  return (
    <div data-testid="blog-editor" className="space-y-6">
      {/* Two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Content */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white border border-[#E8E8E4] p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">
                {post ? "Edit Post" : "New Post"}
              </h3>
              <button type="button" onClick={onCancel} className="text-xs text-[#6B7280] hover:text-[#0D2D54]">Cancel</button>
            </div>

            {/* Title (H1) */}
            <div>
              <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Page Title (H1) *</label>
              <input
                type="text"
                data-testid="editor-title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-3 py-3 text-xl font-['Outfit'] font-medium text-[#0D2D54] border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
                placeholder="Your blog post title..."
              />
              {kw && !keywordInTitle && (
                <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> Primary keyword not found in title</p>
              )}
            </div>

            {/* Slug */}
            <div>
              <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">URL Slug *</label>
              <div className="flex items-center border border-[#E8E8E4] focus-within:border-[#0D2D54]">
                <span className="text-xs text-[#6B7280] px-3 bg-[#FAFAF8] py-2.5 border-r border-[#E8E8E4]">/blog/</span>
                <input
                  type="text"
                  data-testid="editor-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="flex-1 px-3 py-2.5 text-sm text-[#0D2D54] focus:outline-none"
                />
              </div>
              {slug.length > 60 && <p className="text-xs text-yellow-600 mt-1">Slug is long — shorter URLs rank better</p>}
            </div>

            {/* Content Blocks */}
            <div>
              <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-2">Content Blocks</label>
              <div className="space-y-3">
                {blocks.map((block, i) => (
                  <div key={i} className="group relative border border-[#E8E8E4] p-3 hover:border-[#0D2D54]/30 transition-colors">
                    {/* Block header */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] bg-[#FAFAF8] px-2 py-0.5">
                        {block.type.replace("_", " ")}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => moveBlock(i, -1)} className="p-0.5 text-[#6B7280] hover:text-[#0D2D54]" title="Move up"><ChevronUp size={14} /></button>
                        <button type="button" onClick={() => moveBlock(i, 1)} className="p-0.5 text-[#6B7280] hover:text-[#0D2D54]" title="Move down"><ChevronDown size={14} /></button>
                        <button type="button" onClick={() => removeBlock(i)} className="p-0.5 text-[#6B7280] hover:text-red-500" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </div>

                    {/* Block content */}
                    {block.type === "paragraph" && <ParagraphBlock block={block} onChange={(b) => updateBlock(i, b)} />}
                    {block.type === "h2" && <HeadingBlock block={block} onChange={(b) => updateBlock(i, b)} level={2} />}
                    {block.type === "h3" && <HeadingBlock block={block} onChange={(b) => updateBlock(i, b)} level={3} />}
                    {block.type === "bullet_list" && <ListBlock block={block} onChange={(b) => updateBlock(i, b)} ordered={false} />}
                    {block.type === "numbered_list" && <ListBlock block={block} onChange={(b) => updateBlock(i, b)} ordered={true} />}
                    {block.type === "image" && <ImageBlock block={block} onChange={(b) => updateBlock(i, b)} />}
                    {block.type === "faq" && <FAQBlock block={block} onChange={(b) => updateBlock(i, b)} />}

                    {/* Add block button */}
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10">
                      {showAddBlock === i ? (
                        <div className="bg-white border border-[#E8E8E4] shadow-lg p-2 flex gap-1 flex-wrap min-w-[260px]">
                          {BLOCK_TYPES.map((bt) => (
                            <button
                              key={bt.type}
                              type="button"
                              onClick={() => addBlockAt(i, bt.type)}
                              className="px-2 py-1 text-[10px] font-medium text-[#0D2D54] hover:bg-[#FAFAF8] border border-[#E8E8E4] transition-colors"
                            >
                              {bt.label}
                            </button>
                          ))}
                          <button type="button" onClick={() => setShowAddBlock(null)} className="px-2 py-1 text-[10px] text-[#6B7280]">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowAddBlock(i)}
                          className="opacity-0 group-hover:opacity-100 bg-white border border-[#E8E8E4] text-[#6B7280] hover:text-[#0D2D54] hover:border-[#0D2D54] rounded-full w-6 h-6 flex items-center justify-center shadow-sm transition-all"
                        >
                          <Plus size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add first/more blocks */}
                <div className="flex gap-1 flex-wrap pt-2">
                  {BLOCK_TYPES.map((bt) => (
                    <button
                      key={bt.type}
                      type="button"
                      data-testid={`add-block-${bt.type}`}
                      onClick={() => setBlocks([...blocks, createBlock(bt.type)])}
                      className="px-3 py-1.5 text-xs font-medium text-[#6B7280] hover:text-[#0D2D54] border border-[#E8E8E4] hover:border-[#0D2D54] transition-colors"
                    >
                      + {bt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Internal Link Tool */}
            <div className="pt-2">
              <InternalLinkTool blogPosts={blogPosts} onInsert={handleInsertLink} />
            </div>
          </div>
        </div>

        {/* Right: SEO Sidebar */}
        <div className="xl:col-span-1 space-y-4">
          {/* SEO Checklist */}
          <SEOChecklist data={seoData} />

          {/* SEO Metadata */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <h4 className="text-sm font-medium text-[#0D2D54]">SEO Metadata</h4>
            <div>
              <div className="flex justify-between">
                <label className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Meta Title</label>
                <span className={`text-[10px] ${(metaTitle || title).length > 60 ? "text-red-500" : "text-[#6B7280]"}`}>
                  {(metaTitle || title).length}/60
                </span>
              </div>
              <input
                type="text"
                data-testid="editor-meta-title"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
                placeholder={title || "Meta title..."}
              />
            </div>
            <div>
              <div className="flex justify-between">
                <label className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Meta Description</label>
                <span className={`text-[10px] ${metaDescription.length > 155 ? "text-red-500" : "text-[#6B7280]"}`}>
                  {metaDescription.length}/155
                </span>
              </div>
              <textarea
                data-testid="editor-meta-desc"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                rows={2}
                className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none resize-none"
                placeholder="Meta description..."
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Primary Keyword *</label>
              <input
                type="text"
                data-testid="editor-primary-kw"
                value={primaryKeyword}
                onChange={(e) => setPrimaryKeyword(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
                placeholder="e.g. virtual staging UK"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Secondary Keywords</label>
              <input
                type="text"
                value={secondaryKeywords}
                onChange={(e) => setSecondaryKeywords(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
                placeholder="keyword1, keyword2..."
              />
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <h4 className="text-sm font-medium text-[#0D2D54]">Featured Image *</h4>
            {featuredImage ? (
              <div className="relative">
                <img src={featuredImage} alt={featuredImageAlt} className="w-full h-32 object-cover border border-[#E8E8E4]" />
                <button type="button" onClick={() => setFeaturedImage("")} className="absolute top-1 right-1 bg-white text-red-500 text-xs px-1.5 py-0.5 border shadow-sm">Remove</button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-[#E8E8E4] p-4 text-center cursor-pointer hover:border-[#0D2D54]">
                <Upload size={16} className="mx-auto mb-1 text-[#6B7280]" />
                <span className="text-xs text-[#6B7280]">{uploadingFeatured ? "Uploading..." : "Upload featured image"}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFeaturedUpload} />
              </label>
            )}
            <input
              type="text"
              value={featuredImageAlt}
              onChange={(e) => setFeaturedImageAlt(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:border-[#0D2D54] focus:outline-none"
              placeholder="Featured image alt text (required)"
            />
          </div>

          {/* Open Graph */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <h4 className="text-sm font-medium text-[#0D2D54]">Social Sharing (OG)</h4>
            <input type="text" value={ogTitle} onChange={(e) => setOgTitle(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:outline-none" placeholder={metaTitle || title || "OG Title (default = meta title)"} />
            <input type="text" value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:outline-none" placeholder={metaDescription || "OG Description (default = meta desc)"} />
          </div>

          {/* Indexing Controls */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <h4 className="text-sm font-medium text-[#0D2D54]">Indexing</h4>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!noindex} onChange={(e) => setNoindex(!e.target.checked)} className="w-3.5 h-3.5" />
              <span className="text-[#0D2D54]">Allow Google to index this page</span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={includeInSitemap} onChange={(e) => setIncludeInSitemap(e.target.checked)} className="w-3.5 h-3.5" />
              <span className="text-[#0D2D54]">Include in sitemap</span>
            </label>
            <div>
              <label className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Canonical URL (optional)</label>
              <input type="text" value={canonicalUrl} onChange={(e) => setCanonicalUrl(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:outline-none" placeholder="Leave blank for self-referencing" />
            </div>
          </div>

          {/* Category & Tags */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <h4 className="text-sm font-medium text-[#0D2D54]">Category & Tags</h4>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:outline-none">
              <option value="Virtual Staging">Virtual Staging</option>
              <option value="Property Marketing">Property Marketing</option>
              <option value="3D Renders">3D Renders</option>
              <option value="Industry News">Industry News</option>
            </select>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-[#E8E8E4] focus:outline-none" placeholder="Tags (comma separated)" />
          </div>

          {/* Publish */}
          <div className="bg-white border border-[#E8E8E4] p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="w-4 h-4" />
              <span className="text-[#0D2D54] font-medium">Published</span>
            </label>
            <button
              type="button"
              data-testid="editor-save-btn"
              onClick={handleSubmit}
              className="w-full bg-[#0D2D54] text-white py-2.5 text-sm font-medium hover:bg-[#1A3D6B] transition-all"
            >
              {post ? "Update Post" : "Publish Post"}
            </button>
            <button type="button" onClick={onCancel} className="w-full border border-[#E8E8E4] text-[#6B7280] py-2 text-xs hover:bg-[#FAFAF8] transition-all">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
